/**
 * Split an analyst paragraph into findings (spec §11). Extractors write a
 * numbered narrative — "…gaps: (1) KITCHEN is a lump sum — break it out;
 * (2) … Overall the budget is usable." — which reads far better as a list.
 * Returns null when the text isn't list-shaped.
 */

const toneOf = (text) => {
  const t = text.trim();
  if (/^CRITICAL/i.test(t)) return "crit";
  if (/—\s*acceptable[.;,]?$/i.test(t) || /\bis acceptable(?=[.;,)]|\s*$)/i.test(t)) return "ok";
  return "warn";
};

const splitHeadline = (text) => {
  const t = text.trim().replace(/[;,]\s*(and)?\s*$/i, "").trim();
  const dash = t.indexOf(" — ");
  if (dash > 0) return { headline: t.slice(0, dash).trim(), body: t.slice(dash + 3).trim() };
  const m = t.match(/^(.+?[.!?])\s+(.+)$/s);
  if (m) return { headline: m[1].trim(), body: m[2].trim() };
  return { headline: t, body: "" };
};

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * @param {string} text
 * @param {object[]} [rows] table rows ({ rawTitle, title, path, valueDisplay, groupId }) to link as refs
 */
export function parseFindings(text, rows) {
  const s = String(text || "").trim();
  if (!s) return null;
  let intro = "";
  let chunks = [];

  const markers = [...s.matchAll(/\((\d{1,2})\)\s*/g)];
  if (markers.length >= 2) {
    intro = s.slice(0, markers[0].index).trim();
    chunks = markers.map((m, i) =>
      s.slice(m.index + m[0].length, i + 1 < markers.length ? markers[i + 1].index : s.length).trim(),
    );
  } else {
    const lines = s.split(/\n+/);
    const isItem = (l) => /^\s*(\d+[.)]|[-•*])\s+/.test(l);
    const first = lines.findIndex(isItem);
    const items = lines.filter(isItem);
    if (items.length < 2) return null;
    intro = lines.slice(0, first).join(" ").trim();
    chunks = items.map((l) => l.replace(/^\s*(\d+[.)]|[-•*])\s+/, "").trim());
  }
  intro = intro.replace(/:\s*$/, ".");

  let outro = "";
  const last = chunks[chunks.length - 1] || "";
  const oi = last.search(/\s(Overall|In summary|In short)\b/);
  if (oi > 0) {
    outro = last.slice(oi).trim();
    chunks[chunks.length - 1] = last.slice(0, oi).trim();
  }

  // Row titles to link, longest first so "HOT W TANK" wins over "TANK".
  const titles = (rows || [])
    .filter((r) => r.rawTitle && r.rawTitle.trim().length >= 3)
    .sort((a, b) => b.rawTitle.length - a.rawTitle.length);

  const findings = chunks
    .filter(Boolean)
    .map((chunk, i) => {
      const { headline, body } = splitHeadline(chunk);
      const matched = [];
      titles.forEach((r) => {
        const t = r.rawTitle.trim();
        if (matched.some((m) => m.rawTitle.toLowerCase().includes(t.toLowerCase()))) return;
        const re = new RegExp(`(^|[^A-Za-z0-9])${escapeRe(t)}(?=$|[^A-Za-z0-9])`, "i");
        const hit = chunk.match(re);
        if (hit) matched.push({ ...r, pos: hit.index });
      });
      return {
        id: `f${i + 1}`,
        tone: toneOf(chunk),
        headline,
        body,
        refs: matched
          .sort((a, b) => a.pos - b.pos)
          .map((r) => ({ path: r.path, label: r.title, display: r.valueDisplay, groupId: r.groupId })),
      };
    });

  return { intro: intro || undefined, findings, outro: outro || undefined };
}

export default parseFindings;
